import _ from "lodash";
import cx from "classnames";

import { useSelector } from "react-redux";
import Actions from "src/components/actionbar";
import React, { useContext, useEffect, useMemo } from "react";

import { Assessment, Course, CourseSummary, CourseValidationError, Lesson, Question } from "src/types/coaching/courses";
import { CourseContext } from "../../context";

import styles from "./menu.module.scss";
import Slider from "src/components/slider";
import { toast } from "react-toastify";
import Reveal from "src/components/animation";
import Button from "src/components/button";
import { useDispatch } from "react-redux";
import { createQuestionInAssessment, deleteCourse, discardCourseDraft, invalidateCourseDeletionState, invalidateCourseUpdateState } from "src/redux/actions/coaching/courses";
import { useLocation, useNavigate } from "react-router-dom";
import { GenerateQuestionsDialog } from "../generateQuestions";
import { Simulation, SimulationSummary } from "src/types/coaching/simulation";
import { Podcast } from "src/types/coaching/podcast";
import { invalidatePodcastUpdateState } from "src/redux/actions/coaching/podcast";
import { invalidateSimulationUpdateState } from "src/redux/actions/coaching/simulation";
import { AssessmentConfiguration } from "src/types/coaching/assessmentConfiguration";
import { invalidateAssessmentConfigurationUpdateState } from "src/redux/actions/coaching/settings";
import userRoleService from "src/utils/userRoles.service";
import { getContinueEditingTooltip } from "src/pages/coaching/utils/continueEditingTooltip";
import { navigateCoachingEditExit, buildEditEntryState, getManageNavigationState } from "src/pages/coaching/utils/manageNavigation";

export interface MenuState {
  primaryAction: 'save' | 'publish' | null;
  secondaryAction: 'grid' | 'list' | 'generate-questions' | null;
}

interface CourseMenuBarProps {
  course: Course;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuStateChange: (menuState: MenuState) => void;
  mode?: "editor" | "viewer";
}

export const CourseMenuBar: React.FC<CourseMenuBarProps> = ({
  course,
  menuState,
  onMenuStateChange,
  mode = "editor",
}) => {
  const courseContext = useContext(CourseContext);
  const id = courseContext?.id;
  const courseContent = courseContext?.courseContent;

  const navigate = useNavigate();
  const location = useLocation();

  const showValidationErrorDialog = courseContext?.showValidationErrorDialog || false;
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = React.useState(false);

  const hasChanges = useMemo(() => {
    return _.get(course, `hasChanges`) as boolean;
  }, [course]);

  const continueEditingTooltip = useMemo(
    () => getContinueEditingTooltip(course),
    [course],
  );

  const { updating, updated } = useSelector((state: any) => {
    const updating = _.get(state.courses, `update.['${id}'].updating`) as boolean;
    const updated = _.get(state.courses, `update.['${id}'].updated`) as boolean;

    return { updating, updated };
  });

  const { publishing, published } = useSelector((state: any) => {
    const publishing = _.get(state.courses, `publish.publishing`) as boolean;
    const published = _.get(state.courses, `publish.published`) as boolean;

    return { publishing, published };
  });

  const errors = useSelector((state: any) => {
    return _.size(_.get(state.courses, 'edit.errors'));
  }, _.isEqual);

  useEffect(() => {
    if (updated) {
      toast.success('Course saved successfully', {
        autoClose: 3000,
      });
    }
  }, [updated]);

  const handleSave = () => {
    onMenuStateChange({
      ...menuState,
      primaryAction: 'save'
    });
  }

  const handlePublish = () => {
    onMenuStateChange({
      ...menuState,
      primaryAction: 'publish'
    });
  }

  const handleDeleteFinish = () => {
    navigate('/coaching/manage');
    setShowDeleteDialog(false);
  }

  const handleDiscardFinish = () => {
    setShowDiscardDialog(false);
  }

  const handlePreview = () => {
    window.open(`/coaching/manage/courses/preview/${course.id}`, '_blank');
  }

  const handleEdit = () => {
    navigate(`/coaching/manage/courses/draft/${course.id}`, {
      state: buildEditEntryState(location.pathname, location.state),
    });
  };

  const ViewerActions = () => {
    const showContinueEditing =
      course.status === "PUBLISHED" && hasChanges;

    return (
      <>
        {userRoleService.canCreateCourse() && (
          <Actions.Button
            className={cx(
              styles.iconButton,
              showContinueEditing ? styles.continueEditing : styles.edit,
            )}
            icon="paperflite-edit"
            title={showContinueEditing ? "continue editing" : "edit"}
            width={showContinueEditing ? 4 : 2}
            tooltip={showContinueEditing ? continueEditingTooltip : undefined}
            onClick={handleEdit}
          />
        )}
      </>
    );
  };

  return (
    <div className={cx(styles.menubar)}>
      <Actions.Container>
        <Actions.Row className={styles.row}>
          {mode === "editor" ? (
            <>
              <Actions.IconButton
                className={styles.iconButton}
                icon="paperflite-delete"
                tooltip="Delete Course"
                testId="delete-course-button"
                onClick={() => setShowDeleteDialog(true)}
              />

              {course.status === 'PUBLISHED' &&
                <Actions.Button
                  className={styles.iconButton}
                  width={3}
                  icon="paperflite-close"
                  title='discard'
                  testId="discard-changes-button"
                  onClick={() => setShowDiscardDialog(true)}
                />
              }

              <Actions.Button
                className={styles.iconButton}
                width={2}
                icon="paperflite-tick-unselected"
                testId="save-course-button"
                title={updating ? "Saving..." : "Save"}
                disabled={updating || publishing}
                onClick={handleSave}
              />

              <Actions.Button
                className={cx(styles.iconButton, styles.publish)}
                width={2}
                icon="paperflite-rocket"
                title={publishing ? "Publishing..." : "Publish"}
                disabled={!hasChanges || updating || publishing}
                testId="publish-course-button"
                onClick={handlePublish}
              />

              <Reveal show={errors > 0}>
                <Actions.Button
                  className={cx(styles.iconButton, styles.errors)}
                  icon="fa-solid fa-triangle-exclamation"
                  width={2}
                  title={`${errors}`}
                  onClick={() => { courseContext.setShowValidationErrorDialog?.(true) }}
                />
              </Reveal>
            </>
          ) : (
            <ViewerActions />
          )}
        </Actions.Row>

        {courseContent?.entityType === 'assessment' && mode === "editor" && (
          <AssessmentMenu course={course} assessment={courseContent} />
        )}
      </Actions.Container>

      <ValidationErrorDialog 
        show={showValidationErrorDialog} 
        onClose={() => courseContext.setShowValidationErrorDialog?.(false)} 
      />

      <DeleteCourseDialog
        show={showDeleteDialog}
        course={course}
        onFinish={handleDeleteFinish}
        onClose={() => setShowDeleteDialog(false)}
      />

      <DiscardCourseDialog
        show={showDiscardDialog}
        course={course}
        onFinish={handleDiscardFinish}
        onClose={() => setShowDiscardDialog(false)}
      />
    </div>
  )
};

interface AssessmentMenuProps {
  course: Course;
  assessment: Assessment;
}

const AssessmentMenu: React.FC<AssessmentMenuProps> = ({ course, assessment }) => {
  const dispatch = useDispatch();
  const courseContext = useContext(CourseContext);
  const [showGenerateQuestions, setShowGenerateQuestions] = React.useState(false);

  const { layout } = useMemo(() => {
    return {
      layout: _.get(courseContext, 'preferences.assessment_layout') || 'grid'
    };
  }, [courseContext]);

  const handleLayoutChange = (layout: 'grid' | 'list') => {
    const preferences = courseContext.preferences || {};
    courseContext.updatePreferences?.({
      ...preferences,
      assessment_layout: layout
    });
  }

  const handleGenerateQuestionsFinish = (questions: Question[]) => {
    const totalQuestions = assessment.questions.length;
    const insertAt = totalQuestions > 0 ? totalQuestions : 0;

    if (_.size(questions) > 0) {
      questions.forEach((question, index) => {
        dispatch(createQuestionInAssessment({
          course: course.id!,
          assessment: assessment.uuid,
          request: question,
          insertAt: insertAt + index
        }));
      });

      toast.success(`${_.size(questions)} Questions generated successfully`, {
        autoClose: 8000,
      });
    }

    setShowGenerateQuestions(false);
  }

  return (
    <>
      <Actions.Row className={styles.row}>
        <Actions.Button
          className={styles.iconButton}
          width={4}
          image={'/images/seek.gif'}
          title="generate questions"
          onClick={() => setShowGenerateQuestions(true)}
        />

        <Actions.Set className={styles.iconButton}>
          <Actions.SetItem
            icon="paperflite-categories"
            tooltip="Grid"
            onClick={() => handleLayoutChange('grid')}
            className={cx(styles.iconButton, {
              [styles.active]: layout === 'grid'
            })}
          />

          <Actions.SetItem
            icon="paperflite-list"
            tooltip="List"
            onClick={() => handleLayoutChange('list')}
            className={cx(styles.iconButton, {
              [styles.active]: layout === 'list'
            })}
          />
        </Actions.Set>
      </Actions.Row>
      <GenerateQuestionsDialog
        show={showGenerateQuestions}
        course={course}
        onFinish={handleGenerateQuestionsFinish}
        onClose={() => setShowGenerateQuestions(false)}
      />
    </>
  );
}

interface ValidationErrorDialogProps {
  show: boolean;
  onClose: () => void;
}

const ValidationErrorDialog: React.FC<ValidationErrorDialogProps> = ({ show, onClose }) => {
  const courseContext = useContext(CourseContext);

  const contents = useSelector((state: any) => {
    const course = _.get(state.courses, 'edit.item') as Course;
    return _.get(course, 'contents') as any[];
  }, _.isEqual);

  const errors = useSelector((state: any) => {
    return _.get(state.courses, 'edit.errors') as CourseValidationError[];
  }, _.isEqual);

  const getEntityForId = (type, uuid) => {
    const index = _.findIndex(contents, (content) => content.uuid === uuid);
    const entity = index >= 0 ? contents[index] : null;
    return {
      entity,
      index
    };
  }
   
  const sections = useMemo(() => {
    const sections: any[] = [];
    const groupErrors = _.groupBy(errors, 'uuid');
    _.forIn(groupErrors, (value, key) => {
      const content = value[0];
      const type = content.type;
      switch (type) {
        case 'lesson': {
          const {index, entity} = getEntityForId('lesson', key);

          sections.push({
            name: `Lesson ${index + 1}`,
            content: entity,
            errors: value
          });
          break;
        }
        case 'podcast': {
          const { index, entity } = getEntityForId('podcast', key);
        
          sections.push({
            name: `Podcast ${index + 1}`,
            content: entity,
            errors: value
          });
          break;
        }
        case 'assessment': {
          const {index, entity} = getEntityForId('assessment', key);

          sections.push({
            name: `Assessment ${index + 1}`,
            content: entity,
            errors: value
          });
          break;
        }
        case 'section': {
          const { index, entity } = getEntityForId('section', key);
          sections.push({
            name: `Section ${index + 1}`,
            content: entity,
            errors: value
          });
          break;
        }
        case 'simulation': {
          const { index, entity } = getEntityForId('simulation', key);
          sections.push({
            name: `Simulation ${index + 1}`,
            content: entity,
            errors: value
          });
          break;
        }
        case 'course':
          sections.push({
            name: 'Course Details',
            errors: value
          });
          break;
        default:
          break;
      }
    });

    return sections;
  }, [errors]);

  const handleFixClicked = (error: CourseValidationError) => {
    onClose();
    courseContext.gotoError(error);
  }

  return (
    <Slider.Dialog width={"550px"} show={show} onClose={onClose}>
      <Slider.Header title="review errors" message="we failed to publish your course. Fix these errors and train again." />
      <Slider.Body>
        <div className={styles.validationsBody}>
          {sections.map((section, index) => {
            return (
              <ValidationSection key={index} 
                name={section.name} 
                content={section.content}
                errors={section.errors} 
                onFixClicked={handleFixClicked} 
              />
            );
          })}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button onClick={onClose}>Close</Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface ValidationSectionProps {
  name: string;
  content?: Lesson | Assessment | null;
  errors: CourseValidationError[];
  // eslint-disable-next-line no-unused-vars
  onFixClicked: (error: CourseValidationError) => void;
}

const ValidationSection: React.FC<ValidationSectionProps> = ({ name, content, errors, onFixClicked }) => {
  const getMessageForEntity = (error: CourseValidationError) => {
    const message = error.message;
    const type = error.type;
    
    if(type === 'assessment') {
      const assessment = content as Assessment;
      const uuid = error.additionalData?.question;
      if (!uuid || _.size(assessment.questions) === 0) return message;
      const questions = assessment.questions || [];
      const index = _.findIndex(questions, (question) => question.uuid === uuid);

      if (index >= 0) return `Question ${index + 1}: ${message}`;
    }

    return message;
  }

  return (
    <div className={styles.validationSection}>
      <div className={styles.title}>{name}</div>
      <div className={styles.errors}>
        {errors.map((error, index) => {
          const message = getMessageForEntity(error);
          return (
            <div key={index} className={styles.error} onClick={() => onFixClicked(error)}>
              <div className={styles.message}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                {message}
              </div>
              <div className={styles.fix}>
                <i className="paperflite-arrow-up"></i>
                fix now
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.separator}></div>
    </div>
  );
}

interface DeleteCourseDialogProps {
  show: boolean;
  course: Course | Simulation | CourseSummary | SimulationSummary;
  onFinish: () => void;
  onClose: () => void;
}

export const DeleteCourseDialog: React.FC<DeleteCourseDialogProps> = ({ show, course, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const deleting = useSelector((state: any) => {
    return show && _.get(state.courses, `deletion['${course.id!}'].deleting`) as boolean;
  });

  const deleted = useSelector((state: any) => {
    return show && _.get(state.courses, `deletion['${course.id!}'].deleted`) as boolean;
  });

  useEffect(() => {
    if (deleted) {
      dispatch(invalidateCourseDeletionState(course.id!));
      onFinish();
    }
  }, [deleted]);

  const handleConfirm = () => {
    dispatch(deleteCourse(course.id!));
  }

  return (
    <Slider.Dialog width={"400px"} show={show} onClose={onClose}>
      <Slider.Header title="deleting..." message={course?.name} />
      <Slider.Body>
        <div className={styles.deleteBody}>
          <div className={styles.message}>
            Are you sure you want to delete this course? This action cannot be undone.
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary" icon="paperflite-tick" 
          disabled={deleting} 
          testId="delete-course-confirm"
          onClick={handleConfirm}>
            {deleting ? 'Deleting...' : 'Confirm'}
          </Button>
        <Button disabled={deleting} 
          onClick={onClose}
          testId="delete-course-cancel"
        >
            Cancel
        </Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface DiscardCourseDialogProps {
  show: boolean;
  course: Course;
  onFinish: () => void;
  onClose: () => void;
}

const DiscardCourseDialog: React.FC<DiscardCourseDialogProps> = ({ show, course, onFinish, onClose }) => {
  const dispatch = useDispatch();
  const { updating, updated } = useSelector((state: any) => {
    const updating = _.get(state.courses, `update.['${course.id}'].updating`) as boolean;
    const updated = _.get(state.courses, `update.['${course.id}'].updated`) as boolean;

    return { updating, updated };
  });

  useEffect(() => {
    if (updated) {
      dispatch(invalidateCourseUpdateState());
      onFinish();
    }
  }, [updated]);

  const handleConfirm = () => {
    dispatch(discardCourseDraft(course.id!));
  }

  return (
    <Slider.Dialog width={"550px"} show={show} onClose={onClose}>
      <Slider.Header title="Discarding draft..." message={course.name} />
      <Slider.Body>
        <div className={styles.deleteBody}>
          <div className={styles.message}>
            Are you sure you want to discard changes to this course? This action cannot be undone.
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary" icon="paperflite-tick" disabled={updating} onClick={handleConfirm}>{updating ? 'Discarding...' : 'Confirm'}</Button>
        <Button onClick={onClose} disabled={updating}>Cancel</Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface SimulationMenuBarProps {
  handleNextClick: () => void;
}

export const SimulationMenuBar: React.FC<SimulationMenuBarProps> = ({ handleNextClick }) => {

  return (
    <div className={cx(styles.menubar)}>
      <Actions.Container>
        <Actions.Row className={styles.row}>

          <Actions.Button
            width={2}
            icon="paperflite-right-arrow"
            title={"Next"}
            disabled={false}
            onClick={() => {handleNextClick()}}
          />

          <Actions.Button
            className={styles.cancelButton}
            width={2}
            title={"Cancel"}
            disabled={false}
            onClick={() => {}}
          />
        </Actions.Row>
      </Actions.Container>
    </div>
  )
};
interface SaveLearningsDialogProps {
  show: boolean;
  course: Course | Podcast | AssessmentConfiguration;
  onFinish: () => void;
  onClose: () => void;
  handleSave: () => void;
  type?: string
}

export const SaveLearningsDialog: React.FC<SaveLearningsDialogProps> = ({ show, course, onFinish, onClose, handleSave, type="course" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { from: backPath, rootFrom, returnTo } = getManageNavigationState(location.state);

  const { saving, saved } = useSelector((state: any) => {
    const id = course.id;

    const basePaths: Record<string, string> = {
      course: `courses.update['${id}']`,
      podcast: `coaching.podcasts.update['${id}']`,
      simulation: `coaching.simulations.update['${id}']`,
      scorecards: `coaching.settings.scorecards.update['${id}']`
    };

    const basePath = basePaths[type] || basePaths["course"];

    return {
      saving: _.get(state, `${basePath}.updating`),
      saved: _.get(state, `${basePath}.updated`),
    };
  });

  const handleExitNavigation = () => {
    if (type === "scorecards") {
      navigate("/settings/call-assessments");
      return;
    }

    navigateCoachingEditExit(navigate, {
      backPath,
      rootFrom,
      returnTo,
      entityType: type === "simulation" ? "simulation" : type === "podcast" ? "podcast" : "course",
      entityId: course.id,
      navigationState: location.state,
    });
  };

  const handleDiscard = () => {
    onClose();
    handleExitNavigation();
  };

  useEffect(() => {
    if (!saved) {
      return;
    }

    if (type === "course") {
      dispatch(invalidateCourseUpdateState());
    } else if (type === "podcast") {
      dispatch(invalidatePodcastUpdateState());
    } else if (type === "simulation") {
      dispatch(invalidateSimulationUpdateState());
    } else if (type === "scorecards") {
      dispatch(invalidateAssessmentConfigurationUpdateState());
    }

    onFinish();
    handleExitNavigation();
  }, [saved]);

  return (
    <Slider.Dialog width={"23.75rem"} show={show} onClose={onClose}>
      <Slider.Header title="save changes" message={course.name} />
      <Slider.Body>
        <div className={styles.saveBody}>
          <div className={styles.message}>
            Are you sure you want to exit without saving your changes? Unsaved changes will be lost.
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "saving..." : "save"}
        </Button>
        <Button
          onClick={handleDiscard}
          disabled={saving}
        >
          {type=="simulation" ? "cancel" : "discard"}
        </Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}
